import { CupResponse, CupStatus } from "../../domain/cup.interface";

/**
 * SIMS-8831 — what a treatment plan's "Selected Cups" may offer (FR-42).
 *
 * An inactive cup is never a NEW choice (AC2). One the saved plan already holds stays in the list,
 * selected and marked (AC3): filtering it out would repeat SIMS-8876, where the visible selection
 * was the active records only and the next edit wrote the inactive one out of the saved value.
 * Same rule as payerRetention.ts and cupAnalyteChoices.ts; the server applies it on save.
 */
export const isInactiveCup = (cup: Pick<CupResponse, "status">): boolean =>
	cup.status !== undefined && cup.status !== CupStatus.ACTIVE;

export const selectableCups = (
	cups: CupResponse[],
	retainedIds: string[],
): CupResponse[] => {
	const retained = new Set(retainedIds);
	return cups.filter((c) => !isInactiveCup(c) || retained.has(c._id));
};

/**
 * "name — vendor display", as the design's plan modal (markup L841). `display` is the VENDOR's
 * display name, and the API falls it back to the vendor on every read (SIMS-8962 AC6), so labelling
 * a cup by `display` showed every cup of a vendor under the same vendor name.
 */
export const cupLabel = (
	cup: Pick<CupResponse, "name" | "display" | "vendor">,
): string => {
	const vendor = cup.display || cup.vendor;
	return vendor ? `${cup.name} — ${vendor}` : cup.name;
};

/**
 * Every `selectedCups` id anywhere in a saved plan: its treatmentConfig, the templates, and their
 * additional configs. Retention is per plan, matching the server's check.
 */
export const collectPlanCupIds = (plan: unknown): string[] => {
	const ids = new Set<string>();
	const walk = (node: unknown): void => {
		if (Array.isArray(node)) {
			node.forEach(walk);
			return;
		}
		if (!node || typeof node !== "object") return;
		Object.entries(node as Record<string, unknown>).forEach(([key, value]) => {
			if (key === "selectedCups" && Array.isArray(value)) {
				value.forEach((id) => id != null && ids.add(String(id)));
			} else {
				walk(value);
			}
		});
	};
	const p = plan as {
		treatmentConfig?: unknown;
		treatmentConfigOptions?: unknown;
	};
	walk(p?.treatmentConfig);
	walk(p?.treatmentConfigOptions);
	return Array.from(ids);
};

/** The FR-42 refusal from the server: a 400 whose message names the cups. */
export const inactiveCupMessage = (error: unknown): string | null => {
	const e = error as {
		statusCode?: number;
		message?: unknown;
		response?: { status?: number; data?: { message?: unknown } };
	};
	const status = e?.statusCode ?? e?.response?.status;
	const message = e?.response ? e.response.data?.message : e?.message;
	return status === 400 &&
		typeof message === "string" &&
		/can't be added to a treatment plan/i.test(message)
		? message
		: null;
};
